import { useMemo, useCallback, useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { headers, renderRow, Row, renderCondensedRow } from './content';
import { Table } from '../Table';
import { Loader } from '../Loader';
import useLayout from '../../contexts/LayoutContext';
import useModal from '../../contexts/ModalContext';
import useMarkets from '../../contexts/MarketsContext';
import useUser from '../../contexts/UserContext';
import { ClosePositionModalContent } from '../Modal/ClosePositionModal';
import { HydratedPosition, hydratePositionData } from '../../utils/data';
import useWeb3 from '../../contexts/Web3Context';

const StyledLoader = styled(Loader)`
  margin: auto;
  color: ${({ theme }) => theme.gray2};
  background: ${({ theme }) => theme.gray2};

  &:before,
  :after {
    background: ${({ theme }) => theme.gray2};
  }
`;

const LoaderContainer = styled.div`
  height: 80%;
  width: auto;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MobileContainer = styled.div`
  height: 100%;
  overflow: scroll;
`;

const DesktopContainer = styled.div`
  height: 100%;
  overflow: scroll;
`;

interface OpenPositionProps {
  filter?: (position: HydratedPosition) => boolean;
}

export function OpenPositions({ filter }: OpenPositionProps) {
  const { isMobile, isTablet } = useLayout();
  const {
    allPositions,
    reserveValue,
    minMargin,
    fetching,
    deepFetching,
    refreshGlobalState,
    forceNewTraderPosition,
  } = useUser();
  const { address, provider, chainConfig } = useWeb3();
  const { setModal, closeModal } = useModal();
  const { marketList, refreshMarketInfo } = useMarkets();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [data, setData] = useState<HydratedPosition[]>();

  const openClosePositionModal = useCallback(
    (position: HydratedPosition) => {
      const market = marketList.find((item) => position.marketId === item.id);
      if (setModal && market) {
        setModal({
          title: 'Close position',
          content: (
            <ClosePositionModalContent
              market={market}
              position={position}
              onTxFinish={() => {
                forceNewTraderPosition(position.marketId);
                refreshMarketInfo();
                refreshGlobalState();
                closeModal();
              }}
            />
          ),
        });
      }
    },
    [
      setModal,
      closeModal,
      marketList,
      refreshGlobalState,
      refreshMarketInfo,
      forceNewTraderPosition,
    ],
  );

  const handleRowClick = useCallback(
    (row: Row) => {
      const marketId = row.id.match(/[0-9]+$/g)?.[0];
      navigate(`/trade/${marketId}/force`, { replace: true });
    },
    [navigate],
  );

  useEffect(() => {
    let subscribed = true;
    if (allPositions && minMargin && reserveValue && address && provider && chainConfig) {
      Promise.all(
        Object.values(allPositions)
          .flat()
          .map((position) =>
            hydratePositionData({
              minMargin,
              reserveValue,
              position,
              marketList,
              userAddress: address,
              provider,
              chainConfig,
            }),
          ),
      )
        .then((res) => {
          if (subscribed) {
            setData(res.filter((position) => position !== null) as HydratedPosition[]);
          }
        })
        .catch(console.error);
    }
    return () => {
      subscribed = false;
    };
  }, [allPositions, minMargin, reserveValue, address, provider, chainConfig, marketList]);

  const filteredData = useMemo<HydratedPosition[] | undefined>(
    () => (filter && data ? data.filter(filter) : data),
    [data, filter],
  );

  const renderedRows = useMemo<Row[] | null>(() => {
    if (filteredData && !isMobile && !isTablet) {
      return filteredData.map((position) => renderRow(position, openClosePositionModal, t));
    }
    return null;
  }, [filteredData, isTablet, isMobile, openClosePositionModal, t]);

  const renderedCondensedRows = useMemo<JSX.Element[] | null>(() => {
    if (filteredData && (isMobile || isTablet)) {
      return filteredData.map((position) =>
        renderCondensedRow(position, openClosePositionModal, t),
      );
    }
    return null;
  }, [filteredData, isTablet, isMobile, openClosePositionModal, t]);

  if (fetching && !deepFetching) {
    return (
      <LoaderContainer>
        <StyledLoader />
      </LoaderContainer>
    );
  }

  if (!isMobile && !isTablet && renderedRows) {
    return (
      <DesktopContainer>
        <Table headerData={headers(t)} data={renderedRows} onRowClick={handleRowClick} />
      </DesktopContainer>
    );
  }
  if (renderedCondensedRows) {
    return <MobileContainer>{renderedCondensedRows}</MobileContainer>;
  }
  return null;
}

OpenPositions.defaultProps = {
  filter: (position: HydratedPosition) => position,
};
